import { isMapId, levelAt, mapInfo, type MapId } from "@69ly/maps";
import type { Combo } from "@69ly/shared";
import { useTranslation } from "react-i18next";
import { useRef, useState } from "react";
import { Thumbs } from "../components/StuffMedia.tsx";
import { Link, useNavigate, useParams } from "react-router";
import { LandingList } from "../components/AreaField.tsx";
import { BackLink } from "../components/BackLink.tsx";
import { StuffLink } from "../components/StuffDrawer.tsx";
import { ComboGlyphs, ComboMedia, ComboOnMap, ComboStand, comboSpot, defaultComboShow, type ComboShow } from "../components/ComboBits.tsx";
import { Icon } from "../components/Icon.tsx";
import { GrenadeIcon } from "../components/MarkerIcon.tsx";
import { Technique } from "../components/Technique.tsx";
import { focusOn, Radar } from "../components/Radar.tsx";
import { StatusControl } from "../components/StatusControl.tsx";
import { ErrorState, Loading } from "../components/Status.tsx";
import { WindowsBar } from "../components/WindowsBar.tsx";
import { useCallouts } from "../lib/callouts.ts";
import { errorText } from "../lib/errors.ts";
import { useCombo, useDeleteCombo } from "../lib/teamplay-queries.ts";
import { ImportanceTag } from "../components/Importance.tsx";

function ComboMap({ map, combo, show, onShow }: { map: MapId; combo: Combo; show: ComboShow; onShow: (s: ComboShow) => void }) {
  const { t } = useTranslation();
  const spot = comboSpot(combo);
  const level = spot ? levelAt(map, spot.z) : "upper";
  const focus = focusOn(map, combo.steps.flatMap((s) => [s.stuff.throw, s.stuff.landing]));
  return (
    <section className="card">
      <h2 className="eyebrow">{t("detail.map")}</h2>
      <div className="mini-radar">
        <Radar turnable map={map} mapName={mapInfo(map).name} level={level} focus={focus}>
          <ComboOnMap map={map} combo={combo} level={level} active={typeof show === "number" ? show : null} onStep={onShow} />
        </Radar>
      </div>
    </section>
  );
}

function ComboView({ combo }: { combo: Combo }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const remove = useDeleteCombo();
  const map = isMapId(combo.map) ? combo.map : null;
  const spot = comboSpot(combo);
  const [show, setShow] = useState<ComboShow>(() => defaultComboShow(combo));
  const media = useRef<HTMLElement>(null);

  return (
    <div className="page detail">
      {map && <BackLink fallback={`/library/${map}?c=${combo.id}`} fallbackLabel={t("detail.back", { map: mapInfo(map).name })} />}
      <div className="page-head">
        <div className="detail-title">
          <ComboGlyphs combo={combo} size={16} />
          <div>
            <h1 className="page-title">{combo.title}</h1>
            <p className="muted">
              <span className={`side-tag side-${combo.side}`}>{t(`sideShort.${combo.side}`)}</span> <ImportanceTag importance={combo.importance} /> {t("combo.kind")} ·{" "}
              {t("combo.throws", { count: combo.steps.length })} · {t(`area.${combo.area}`)} ·{" "}
              {t("mapView.by", { name: combo.author.name })}
            </p>
            <ComboPlaces combo={combo} />
          </div>
        </div>
        {combo.canEdit && (
          <div className="row">
            <Link to={`/combos/${combo.id}/edit`} className="btn btn-secondary">
              {t("mapView.edit")}
            </Link>
            <button
              type="button"
              className="btn btn-quiet btn-danger"
              disabled={remove.isPending}
              onClick={() => {
                if (window.confirm(t("comboDetail.confirmDelete", { title: combo.title })))
                  remove.mutate(combo.id, { onSuccess: () => navigate(map ? `/library/${map}` : "/library", { replace: true }) });
              }}
            >
              {t("detail.delete")}
            </button>
          </div>
        )}
      </div>

      <div className="columns">
        <div className="column-main">
          <section className="card" ref={media}>
            <ComboMedia combo={combo} show={show} onShow={setShow} />
          </section>
          {spot && (
            <section className="card">
              <ComboStand combo={combo} show={show} heading={(title) => <h2 className="eyebrow">{title}</h2>} />
            </section>
          )}
          <ol className="combo-detail-steps">
            {combo.steps.map((s, i) => {
              return (
                <li key={s.stuff.id} className={`card combo-detail-step${show === i ? " is-active" : ""}`}>
                  <div className="learn-card-head">
                    <span className="step-number">{i + 1}</span>
                    <GrenadeIcon grenade={s.stuff.grenade} side={combo.side} size={28} />
                    <div className="learn-card-title">
                      <h2 className="panel-title">
                        <StuffLink id={s.stuff.id}>{s.stuff.title}</StuffLink>
                      </h2>
                    </div>
                  </div>
                  <Technique technique={s.stuff.technique} compact />
                  {s.note && <p className="step-note">{s.note}</p>}
                  <Thumbs stuff={s.stuff} />
                  {show !== i && (
                    <button
                      type="button"
                      className="btn btn-quiet btn-small combo-show-above"
                      onClick={() => {
                        setShow(i);
                        media.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                      }}
                    >
                      <Icon name="play" size={13} />
                      {t("comboMedia.showAbove")}
                    </button>
                  )}
                  <StatusControl stuffId={s.stuff.id} status={s.stuff.myStatus} />
                </li>
              );
            })}
          </ol>
          {combo.notes && (
            <section className="card">
              <h2 className="eyebrow">{t("detail.notes")}</h2>
              <p className="notes">{combo.notes}</p>
            </section>
          )}
        </div>
        <aside className="column-side">
          {map && <ComboMap map={map} combo={combo} show={show} onShow={setShow} />}
          <section className="card">
            <h2 className="eyebrow">{t("mapView.windows")}</h2>
            <WindowsBar windows={combo.windows} />
          </section>
          <section className="card">
            <h2 className="eyebrow">{t("mapView.myStatus")}</h2>
            <p>{t("combo.learnedOf", { learned: combo.learnedByMe, total: combo.steps.length })}</p>
            <p className="muted small">{t("comboDetail.statusHelp")}</p>
          </section>
        </aside>
      </div>
    </div>
  );
}

export function ComboDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const combo = useCombo(id);
  if (combo.isPending) return <Loading />;
  if (combo.error) return <ErrorState message={errorText(t, combo.error)} onRetry={() => void combo.refetch()} />;
  return <ComboView combo={combo.data} />;
}

/** Where the combo is thrown from, and every distinct callout it lands on. */
function ComboPlaces({ combo }: { combo: Combo }) {
  const { t } = useTranslation();
  const callouts = useCallouts();
  if (!isMapId(combo.map)) return null;
  const map = combo.map;
  const first = combo.steps[0]?.stuff;
  const from = first ? callouts.places(map, first).from : null;
  return (
    <div className="combo-places">
      {from && (
        <p className="small">
          {t("places.thrownFrom")} <b>{from}</b>
        </p>
      )}
      <LandingList places={combo.steps.map((s) => callouts.places(map, s.stuff).to)} />
    </div>
  );
}
